import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Slider,
  TextField,
  Typography
} from '@mui/material';
import { getAuth } from 'firebase/auth';
import BASE_URL from '../../config';
import TileGallery from './TileGallery';

const toInputDate = (date) => date.toISOString().slice(0, 10);

const getDefaultStartDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 90);
  return toInputDate(date);
};

const getDefaultEndDate = () => toInputDate(new Date());

function SentinelAnalysisPanel({ geometryFeature, selectedSiteCode, selectedOrgId, onAnalysisComplete }) {
  const [startDate, setStartDate] = useState(getDefaultStartDate);
  const [endDate, setEndDate] = useState(getDefaultEndDate);
  const [maxCloud, setMaxCloud] = useState(80);
  const [threshold, setThreshold] = useState(0.3);
  const [scenes, setScenes] = useState([]);
  const [selectedScenes, setSelectedScenes] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  const geometry = geometryFeature?.geometry;
  const hasGeometry = Boolean(geometry?.type && geometry?.coordinates);

  // S1: clear stale search results when the selected site changes so results
  // from a previously-searched site can't be analyzed against a new site's geometry.
  useEffect(() => {
    setScenes([]);
    setSelectedScenes([]);
    setHasSearched(false);
    setError('');
    setWarning('');
  }, [selectedSiteCode]);

  const getHeaders = async () => {
    const user = getAuth().currentUser;
    const token = user && await user.getIdToken();

    if (!token) {
      throw new Error('You must be signed in to run vegetation analysis.');
    }

    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    };
  };

  const postSentinel = async (path, payload) => {
    const response = await fetch(`${BASE_URL}/api/sentinel/${path}`, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify({
        ...payload,
        organisation_id: selectedOrgId
      })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Vegetation analysis request failed.');
    }

    return data;
  };

  const handleSearch = async () => {
    setError('');
    setWarning('');

    if (!hasGeometry) {
      setError('Select a lake with a saved polygon before searching for images.');
      return;
    }

    if (!startDate || !endDate || startDate > endDate) {
      setError('Choose a valid date range before searching tiles.');
      return;
    }

    setLoadingSearch(true);
    try {
      const data = await postSentinel('search', {
        geometry,
        startDate,
        endDate,
        maxCloud
      });
      const nextScenes = data.scenes || [];
      setScenes(nextScenes);
      setHasSearched(true);
      setSelectedScenes(
        nextScenes
          .filter((scene) => Number(scene.cloudCover ?? 100) <= 50)
          .map((scene) => ({ id: scene.id, date: scene.date, cloudCover: scene.cloudCover }))
      );
    } catch (searchError) {
      setError(searchError.message);
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleToggleScene = (scene) => {
    setSelectedScenes((current) => (
      current.some((selected) => selected.id === scene.id)
        ? current.filter((selected) => selected.id !== scene.id)
        : [...current, { id: scene.id, date: scene.date, cloudCover: scene.cloudCover }]
    ));
  };

  const handleAnalyze = async () => {
    setError('');
    setWarning('');

    if (!selectedScenes.length) {
      setError('Select at least one image before running vegetation analysis.');
      return;
    }

    setLoadingAnalysis(true);
    try {
      const data = await postSentinel('analyze', {
        geometry,
        scenes: selectedScenes,
        siteId: selectedSiteCode,
        threshold
      });
      if (data.warning) {
        setWarning(data.warning);
      }

      // Refresh the parent's run list so the new analysis date(s) appear in
      // the existing run dropdown (parent auto-selects the newest run).
      if (onAnalysisComplete) {
        onAnalysisComplete(data);
      }
    } catch (analysisError) {
      setError(analysisError.message);
    } finally {
      setLoadingAnalysis(false);
    }
  };

  return (
    <Box sx={{ p: 2, borderTop: '1px solid #f1f5f9', backgroundColor: 'background.paper' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Vegetation Analysis
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1.5 }}>
        Uses the selected lake boundary as the analysis area.
      </Typography>

      {!hasGeometry && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Select a lake with polygon data to enable tile search.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {warning && <Alert severity="warning" sx={{ mb: 1.5 }}>{warning}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.5 }}>
        <TextField
          label="Start"
          type="date"
          size="small"
          value={startDate}
          onChange={(event) => setStartDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End"
          type="date"
          size="small"
          value={endDate}
          onChange={(event) => setEndDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Max cloud cover: {maxCloud}%
        </Typography>
        <Slider
          size="small"
          min={0}
          max={100}
          step={5}
          value={maxCloud}
          onChange={(_, value) => setMaxCloud(value)}
          valueLabelDisplay="auto"
        />
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Vegetation Threshold &gt; {threshold.toFixed(2)}
        </Typography>
        <Slider
          size="small"
          min={0}
          max={1}
          step={0.05}
          value={threshold}
          onChange={(_, value) => setThreshold(value)}
          valueLabelDisplay="auto"
        />
      </Box>

      <Button
        fullWidth
        variant="contained"
        size="small"
        disabled={!hasGeometry || loadingSearch}
        onClick={handleSearch}
      >
        {loadingSearch ? <CircularProgress size={18} color="inherit" /> : 'Search Tiles'}
      </Button>

      {hasSearched && (
        <TileGallery
          scenes={scenes}
          selectedSceneIds={selectedScenes.map((scene) => scene.id)}
          onToggleScene={handleToggleScene}
        />
      )}

      {scenes.length > 0 && (
        <Button
          fullWidth
          variant="contained"
          color="success"
          size="small"
          disabled={!selectedScenes.length || loadingAnalysis}
          onClick={handleAnalyze}
          sx={{ mt: 1.5 }}
        >
          {loadingAnalysis ? <CircularProgress size={18} color="inherit" /> : `Run Analysis (${selectedScenes.length})`}
        </Button>
      )}

    </Box>
  );
}

export default SentinelAnalysisPanel;
